import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useAuth } from '../hooks/useAuth';
import {
  handleCallback,
  AuthError,
  getWorkspaceSwitchTarget,
  clearWorkspaceSwitchState,
  hasToken,
} from '../lib/centralAuth';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';

interface AuthErrorState {
  message: string;
  errorCode: string;
  httpStatus?: number;
}

/**
 * Module-level set of authorization codes that have already been consumed by
 * this tab. Prevents:
 *   - React StrictMode double-mount (first mount consumes verifier, second
 *     mount would fail with MISSING_CODE_VERIFIER).
 *   - Manual reload / back-forward navigation to /auth/callback with the same
 *     code, which Central rejects as TOKEN_INVALID_GRANT (codes are single-use).
 * Lives for the lifetime of the tab (fine — codes expire in ~60s anyway).
 */
const consumedCodes = new Set<string>();

export function AuthCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { login } = useAuth();
  const [error, setError] = useState<AuthErrorState | null>(null);
  const processingRef = useRef(false);

  useEffect(() => {
    if (processingRef.current) return;
    processingRef.current = true;

    const code = searchParams.get('code');

    // If we already have a valid token, this callback URL is stale
    // (e.g. user hit back after a successful sign-in). Just go to dashboard.
    if (hasToken()) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // If we've already consumed this specific code in this tab, don't retry
    // — Central will reject the second exchange as invalid_grant.
    if (code && consumedCodes.has(code)) {
      navigate('/dashboard', { replace: true });
      return;
    }

    async function processCallback() {
      try {
        const workspaceSwitchTarget = getWorkspaceSwitchTarget();

        // Mark the code consumed BEFORE exchange so a concurrent retry cannot
        // attempt to exchange it again.
        if (code) consumedCodes.add(code);

        const token = await handleCallback(searchParams);

        if (workspaceSwitchTarget) {
          console.log('Workspace switch completed:', workspaceSwitchTarget);
          queryClient.clear();
          clearWorkspaceSwitchState();
        }

        login(token);

        // Navigate to dashboard and strip the code/state params from history
        // so a reload/back doesn't replay the already-consumed code.
        navigate('/dashboard', { replace: true });
      } catch (err) {
        clearWorkspaceSwitchState();

        if (err instanceof AuthError) {
          setError({
            message: err.message,
            errorCode: err.errorCode,
            httpStatus: err.httpStatus,
          });
        } else {
          setError({
            message: err instanceof Error ? err.message : 'Authentication failed',
            errorCode: 'UNKNOWN_ERROR',
          });
        }
      }
    }

    processCallback();
  }, [searchParams, login, navigate, queryClient]);

  if (error) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50">
        <div className="text-center max-w-md p-8 bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2">
            Authentication Failed
          </h1>
          <p className="text-gray-600 mb-4">{error.message}</p>
          <div className="text-xs text-gray-400 mb-6 font-mono">
            {error.errorCode}
            {error.httpStatus && ` (HTTP ${error.httpStatus})`}
          </div>
          <button
            className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors"
            onClick={() => navigate('/', { replace: true })}
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen items-center justify-center">
      <div className="text-center">
        <LoadingSpinner size="lg" />
        <p className="mt-4 text-gray-600">Completing sign in...</p>
      </div>
    </div>
  );
}
